/**
 * SiteFooter — the site-wide public footer.
 *
 * Mounted by AppRouter (App.js) after <Routes> on every public marketing
 * path, so individual pages never render their own footer.
 *
 * Contents (contest brief, 2026-07-11):
 *   - Brand block + the canonical privacy promise, QUOTED from CANON
 *     block 1 — never paraphrased.
 *   - Four link columns spanning the whole public surface.
 *   - Bottom bar: © line, "Midnite Productions" attribution (no "LLC" —
 *     the entity isn't formed), contact email, and the honest
 *     "Built with Emergent" hosting credit → emergent.sh.
 *
 * Copy law: no "free", no "sync", no "verified", no medical/clinical
 * claims. The safety disclaimer lives in TickerBanner, not here.
 */
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

const COLUMNS = [
  {
    title: "Product",
    links: [
      { to: "/demo", label: "Live demo" },
      { to: "/pricing", label: "Pricing" },
      { to: "/ready-boards", label: "Ready Boards" },
      { to: "/download", label: "Get the app" },
      { to: "/whats-new", label: "What's new" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about-us", label: "About us" },
      { to: "/contact", label: "Contact" },
      { to: "/blog", label: "Learn & grow" },
      { to: "/community", label: "Community" },
    ],
  },
  {
    title: "Privacy & trust",
    links: [
      { to: "/privacy-promise", label: "Our privacy promise" },
      { to: "/security", label: "Security" },
      { to: "/data-protection", label: "Data protection" },
      { to: "/privacy", label: "Privacy policy" },
      { to: "/terms", label: "Terms of service" },
    ],
  },
  {
    title: "For professionals",
    links: [
      { to: "/therapist-portal", label: "For therapists" },
      { to: "/schools-portal", label: "For schools" },
      { to: "/therapists", label: "Therapist directory" },
      { to: "/demo-safety", label: "Demo & safety notice" },
    ],
  },
];

export default function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-[color:var(--cream)] text-[color:var(--ink)] border-t-[4px] border-[color:var(--coral)] mt-0"
      data-testid="site-footer"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_2fr] gap-10 md:gap-14">
          {/* Brand + the canonical promise */}
          <div>
            <Link
              to="/"
              className="inline-block display text-2xl font-extrabold text-[color:var(--ink)] hover:text-[color:var(--coral-deep)] transition-colors"
              data-testid="footer-brand"
            >
              SonSpeak
            </Link>
            <p className="text-sm text-[color:var(--ink)]/70 mt-1">
              Empowering every voice.
            </p>
            <blockquote
              className="mt-5 border-l-4 border-[color:var(--coral)] pl-4"
              data-testid="footer-privacy-promise"
            >
              <p className="display font-bold text-base leading-snug">
                &ldquo;Zero remotely readable identifying child content.&rdquo;
              </p>
              <p className="text-xs text-[color:var(--ink)]/70 mt-2 leading-relaxed">
                Everything your child or family creates lives encrypted on your
                device. That is the promise the whole product is built around.
              </p>
              <Link
                to="/privacy-promise"
                className="inline-block text-xs font-semibold underline underline-offset-2 mt-2 text-[color:var(--coral-deep)] hover:text-[color:var(--ink)] transition-colors"
                data-testid="footer-privacy-promise-link"
              >
                Read the full promise
              </Link>
            </blockquote>
          </div>

          {/* Link columns */}
          <nav aria-label="Footer" className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <h2 className="display text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ink)]/55 mb-3">
                  {col.title}
                </h2>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="text-sm text-[color:var(--ink)]/80 hover:text-[color:var(--coral-deep)] hover:underline underline-offset-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--coral)] rounded-sm"
                        data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-[color:var(--ink)]/15 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs text-[color:var(--ink)]/65">
          <span data-testid="footer-copyright">
            © {year} SonSpeak · Midnite Productions
          </span>
          <a
            href="mailto:hello@sonspeak.com"
            className="inline-flex items-center gap-1.5 hover:text-[color:var(--coral-deep)] underline underline-offset-2 transition-colors"
            data-testid="footer-contact-email"
          >
            <Mail size={12} aria-hidden /> hello@sonspeak.com
          </a>
          <span className="hidden sm:block flex-1" aria-hidden />
          <a
            href="https://emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-[color:var(--coral-deep)] transition-colors"
            data-testid="footer-emergent-badge"
          >
            <span
              aria-hidden
              className="inline-block w-2 h-2 rounded-full bg-[color:var(--gold)]"
            />
            Built with Emergent
          </a>
        </div>
      </div>
    </footer>
  );
}
